"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { gql, type TypedDocumentNode } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useConstantRefCallback } from "@jasperlabs/hooks";

import { useSession, useSessionRemovedListener } from "@/contexts/session";
import { analytics } from "@/lib/analytics";
import {
	isPosthogEnabled,
	posthog,
	resetPosthog,
	registerTenantGroup,
	type InvestorPersonProperties,
} from "@/lib/posthog";
import { useActiveTenant } from "@/tenants/TenantProvider";

import useAuthenticatedUser from "../useAuthenticatedUser";
import type { AnalyticsAdviserProfileQuery } from "./Analytics.generated";

const ADVISER_PROFILE_QUERY = gql`
	query AnalyticsAdviserProfile {
		account {
			id
			adviserProfile {
				id
				registrationCountry {
					id
					name
				}
			}
		}
	}
`;

const useUnmountCleanup = (cleanup: () => void) => {
	const cleanupRef = useConstantRefCallback(cleanup);

	useEffect(() => cleanupRef, [cleanupRef]);
};

export function Analytics() {
	const user = useAuthenticatedUser();
	const pathname = usePathname();
	const tenant = useActiveTenant();
	const { hasAuthenticatedSession } = useSession();

	const tenantId = tenant.tenantId ?? undefined;
	const { id: userId } = user ?? {};

	const {
		data: adviserData,
		loading: adviserLoading,
		error: adviserError,
	} = useQuery(
		ADVISER_PROFILE_QUERY as TypedDocumentNode<AnalyticsAdviserProfileQuery>,
		{
			skip: !hasAuthenticatedSession,
		},
	);
	const adviserProfile = adviserData?.account?.adviserProfile ?? null;
	const adviserResolved = !adviserLoading && !adviserError;

	/**
	 * Identify authenticated users in PostHog with tenant and adviser properties.
	 * Adviser properties are deferred until the query resolves so PostHog can
	 * segment by investor vs adviser without false negatives on error.
	 */
	useEffect(() => {
		if (userId && !adviserLoading) {
			analytics.identify(userId);
			if (isPosthogEnabled()) {
				const properties: InvestorPersonProperties = {
					tenantId,
					tenantName: tenant.tenantName,
					tenantCountry: tenant.tenantCountry,
					...(adviserResolved && {
						isAdviser: Boolean(adviserProfile),
						...(adviserProfile && {
							adviserRegistrationCountry:
								adviserProfile.registrationCountry?.name,
						}),
					}),
				};
				posthog.identify(userId, properties);
				if (tenantId) {
					registerTenantGroup(tenantId, {
						name: tenant.tenantName,
						country: tenant.tenantCountry,
					});
				}
			}
		}
	}, [
		tenantId,
		userId,
		tenant.tenantName,
		tenant.tenantCountry,
		adviserProfile,
		adviserLoading,
		adviserResolved,
	]);

	/**
	 * Track Segment page views (PostHog pageview is in PostHogProvider/PostHogPageView)
	 */
	useEffect(() => {
		const id = setTimeout(() => {
			analytics.page();
		}, 1000);

		return () => {
			clearTimeout(id);
		};
	}, [pathname]);

	/**
	 * When the user ends their session, reset
	 */
	useSessionRemovedListener(() => {
		analytics.reset();
		resetPosthog();
	});

	/**
	 * When this component unmounts for any reason, reset
	 */
	useUnmountCleanup(() => {
		analytics.reset();
		resetPosthog();
	});

	return null;
}
